import { lazy, Suspense, type ComponentType } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { ProtectedRoute } from '@/auth/protected-route'
import { PermissionRoute } from '@/auth/permission-route'
import { AppShell } from '@/components/layout/app-shell'
import { LoadingState } from '@/components/shared/states'

type PageModule = Record<string, ComponentType>

function lazyPage(importer: () => Promise<PageModule>, exportName: string) {
  return lazy(async () => {
    const module = await importer()
    return { default: module[exportName] }
  })
}

const LoginPage = lazyPage(() => import('@/pages/login-page'), 'LoginPage')
const SetPasswordPage = lazyPage(() => import('@/pages/set-password-page'), 'SetPasswordPage')
const PublicProgrammeAttendancePage = lazyPage(() => import('@/pages/public-programme-attendance-page'), 'PublicProgrammeAttendancePage')
const PublicProgrammeFeedbackPage = lazyPage(() => import('@/pages/public-programme-feedback-page'), 'PublicProgrammeFeedbackPage')
const DashboardPage = lazyPage(() => import('@/pages/dashboard-page'), 'DashboardPage')
const EmployeesPage = lazyPage(() => import('@/pages/employees-page'), 'EmployeesPage')
const EmployeeDetailPage = lazyPage(() => import('@/pages/employee-detail-page'), 'EmployeeDetailPage')
const RolesPage = lazyPage(() => import('@/pages/roles-page'), 'RolesPage')
const CrmPage = lazyPage(() => import('@/pages/crm-page'), 'CrmPage')
const OrganizationDetailPage = lazyPage(() => import('@/pages/organization-detail-page'), 'OrganizationDetailPage')
const ContactDetailPage = lazyPage(() => import('@/pages/contact-detail-page'), 'ContactDetailPage')
const OpportunitiesPage = lazyPage(() => import('@/pages/opportunities-page'), 'OpportunitiesPage')
const ProposalsPage = lazyPage(() => import('@/pages/proposals-page'), 'ProposalsPage')
const ProposalDetailPage = lazyPage(() => import('@/pages/proposal-detail-page'), 'ProposalDetailPage')
const OutreachPage = lazyPage(() => import('@/pages/outreach-page'), 'OutreachPage')
const ProgrammesPage = lazyPage(() => import('@/pages/programmes-page'), 'ProgrammesPage')
const ProgrammeDetailPage = lazyPage(() => import('@/pages/programme-detail-page'), 'ProgrammeDetailPage')
const TasksPage = lazyPage(() => import('@/pages/tasks-page'), 'TasksPage')
const TaskDetailPage = lazyPage(() => import('@/pages/task-detail-page'), 'TaskDetailPage')
const TprsPage = lazyPage(() => import('@/pages/tprs-page'), 'TprsPage')
 const WeeklyReportDetailPage = lazyPage(() => import('@/pages/weekly-report-detail-page'), 'WeeklyReportDetailPage')
 const LeavePage = lazyPage(() => import('@/pages/leave-page'), 'LeavePage')
const AttendancePage = lazyPage(() => import('@/pages/attendance-page'), 'AttendancePage')
const EapPage = lazyPage(() => import('@/pages/eap-page'), 'EapPage')
const EapCaseDetailPage = lazyPage(() => import('@/pages/eap-case-detail-page'), 'EapCaseDetailPage')
const AdminPage = lazyPage(() => import('@/pages/admin-page'), 'AdminPage')
const DocumentsPage = lazyPage(() => import('@/pages/documents-page'), 'DocumentsPage')
const QrStudioPage = lazyPage(() => import('@/pages/qr-studio-page'), 'QrStudioPage')
const ToolsPage = lazyPage(() => import('@/pages/tools-page'), 'ToolsPage')
const DocumentConverterPage = lazyPage(() => import('@/pages/document-converter-page'), 'DocumentConverterPage')
const ProgrammeFormsPage = lazyPage(() => import('@/pages/programme-forms-page'), 'ProgrammeFormsPage')
const CertificateGeneratorPage = lazyPage(() => import('@/pages/certificate-generator-page'), 'CertificateGeneratorPage')
const SlideStudioPage = lazyPage(() => import('@/pages/slide-studio-page'), 'SlideStudioPage')
const SignatureStampPage = lazyPage(() => import('@/pages/signature-stamp-page'), 'SignatureStampPage')
const ReportsPage = lazyPage(() => import('@/pages/reports-page'), 'ReportsPage')
const AiPage = lazyPage(() => import('@/pages/ai-page'), 'AiPage')
const NotificationsPage = lazyPage(() => import('@/pages/notifications-page'), 'NotificationsPage')
const AuditLogsPage = lazyPage(() => import('@/pages/audit-logs-page'), 'AuditLogsPage')
const SettingsPage = lazyPage(() => import('@/pages/settings-page'), 'SettingsPage')
const InvoicesPage = lazyPage(() => import('@/pages/invoices-page'), 'InvoicesPage')
const InvoiceDetailPage = lazyPage(() => import('@/pages/invoice-detail-page'), 'InvoiceDetailPage')
const NotFoundPage = lazyPage(() => import('@/pages/not-found-page'), 'NotFoundPage')

export function App() {
  return <Suspense fallback={<LoadingState label="Opening workspace..." />}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/programme-attendance/:token" element={<PublicProgrammeAttendancePage />} />
      <Route path="/programme-feedback/:token" element={<PublicProgrammeFeedbackPage />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          <Route element={<PermissionRoute><Outlet /></PermissionRoute>}>
            <Route index element={<DashboardPage />} />
            <Route path="employees" element={<EmployeesPage />} />
            <Route path="employees/:id" element={<EmployeeDetailPage />} />
            <Route path="roles" element={<RolesPage />} />
            <Route path="crm" element={<CrmPage />} />
            <Route path="crm/organizations/:id" element={<OrganizationDetailPage />} />
            <Route path="crm/contacts/:id" element={<ContactDetailPage />} />
            <Route path="crm/opportunities" element={<OpportunitiesPage />} />
            <Route path="crm/proposals" element={<ProposalsPage />} />
            <Route path="crm/proposals/:id" element={<ProposalDetailPage />} />
            <Route path="outreach" element={<OutreachPage />} />
            <Route path="programmes" element={<ProgrammesPage />} />
            <Route path="programmes/editions/:id" element={<ProgrammeDetailPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="tasks/:id" element={<TaskDetailPage />} />
          <Route path="tprs" element={<TprsPage />} />
          <Route path="tprs/:id" element={<TprsPage />} />
            <Route path="weekly-reports" element={<Navigate to="/tasks" replace />} />
            <Route path="weekly-reports/review" element={<Navigate to="/tasks" replace />} />
            <Route path="weekly-reports/:id" element={<WeeklyReportDetailPage />} />
            <Route path="leave" element={<LeavePage />} />
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="eap" element={<EapPage />} />
            <Route path="eap/cases/:id" element={<EapCaseDetailPage />} />
            <Route path="admin" element={<AdminPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="qr-studio" element={<QrStudioPage />} />
            <Route path="tools" element={<ToolsPage />} />
            <Route path="tools/document-converter" element={<DocumentConverterPage />} />
            <Route path="tools/programme-forms" element={<ProgrammeFormsPage />} />
            <Route path="tools/certificates" element={<CertificateGeneratorPage />} />
            <Route path="tools/slide-studio" element={<SlideStudioPage />} />
            <Route path="tools/signature-stamp" element={<SignatureStampPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="ai" element={<AiPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="admin/audit-logs" element={<AuditLogsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  </Suspense>
}